import {FormEditor} from "@t/fd/editor/FormEditor.ts";

export type DurationComponent = "days" | "hours" | "minutes" | "seconds" | "milliseconds";

export const ALL_DURATION_COMPONENTS: DurationComponent[] = ["days", "hours", "minutes", "seconds", "milliseconds"];

export interface Duration extends FormEditor {
	type: "duration";
	/** Format of property, defaults to ISO 8601 */
	propertyFormat?: "iso8601" | "minutes" | "seconds" | "milliseconds";
	/** Minimum and maximum duration components. Displays all components if not set. */
	lowest?: DurationComponent;
	highest?: DurationComponent;
}

export type DurationParsed = { d: number; h: number; m: number; s: number; ms: number };

export const ZERO: DurationParsed = { d: 0, h: 0, m: 0, s: 0, ms: 0 };

// ^-?P  optional sign + P
// (?:(-?\d+)D)?  optional days
// (?:T  start of time part
//   (?:(-?\d+)H)?  optional hours
//   (?:(-?\d+)M)?  optional minutes
//   (?:(-?\d+)(?:\.(\d{1,3}))?S)?  optional seconds with up to 3 fractional digits
// )?$
const ISO_DURATION_RE =
	/^(-?)P(?:(-?\d+)D)?(?:T(?:(-?\d+)H)?(?:(-?\d+)M)?(?:(-?\d+)(?:\.(\d{1,9}))?S)?)?$/;

export function parseDuration(iso: string | null | undefined): DurationParsed {
	if (iso == null) return { ...ZERO };

	const match = ISO_DURATION_RE.exec(iso);
	if (!match) {
		throw new Error(`Invalid ISO 8601 duration: ${iso}`);
	}

	const [, sign, dStr, hStr, mStr, sStr, fracStr] = match;

	// Must have at least one component (P alone or PT alone is invalid)
	if (!dStr && !hStr && !mStr && !sStr) {
		throw new Error(`Invalid ISO 8601 duration: ${iso}`);
	}

	const d = dStr ? parseInt(dStr, 10) : 0;
	const h = hStr ? parseInt(hStr, 10) : 0;
	const m = mStr ? parseInt(mStr, 10) : 0;
	const s = sStr ? parseInt(sStr, 10) : 0;

	let ms = 0;
	if (fracStr) {
		// Pad/truncate to 3 digits, preserve sign of seconds
		const padded = (fracStr + "000").slice(0, 3);
		ms = parseInt(padded, 10);
		if (sStr && sStr.startsWith("-")) ms = -ms;
		// Edge case: "PT-0.5S" — sign is on the "0", lost by parseInt
		else if (sStr === "-0") ms = -ms;
	}

	const factor = sign === "-" ? -1 : 1;
	return {
		d: d * factor,
		h: h * factor,
		m: m * factor,
		s: s * factor,
		ms: ms * factor,
	};
}

export function toIsoFormat(duration: DurationParsed): string {
	const { d, h, m, s, ms } = duration;

	if (d === 0 && h === 0 && m === 0 && s === 0 && ms === 0) {
		return "PT0S";
	}

	let result = "P";
	if (d !== 0) result += `${d}D`;

	const hasTimeComponent = h !== 0 || m !== 0 || s !== 0 || ms !== 0;
	if (hasTimeComponent) {
		result += "T";
		if (h !== 0) result += `${h}H`;
		if (m !== 0) result += `${m}M`;

		if (s !== 0 || ms !== 0) {
			if (ms === 0) {
				result += `${s}S`;
			} else {
				// Combine s and ms into a decimal. Both should have same sign for clean output;
				// if they don't (e.g. caller passed inconsistent input), normalize.
				const totalMs = s * 1000 + ms;
				const sign = totalMs < 0 ? "-" : "";
				const absMs = Math.abs(totalMs);
				const wholeS = Math.floor(absMs / 1000);
				const fracMs = absMs % 1000;
				const fracStr = fracMs.toString().padStart(3, "0").replace(/0+$/, "");
				result += `${sign}${wholeS}${fracStr ? "." + fracStr : ""}S`;
			}
		}
	}

	return result;
}

export function toMillis(duration: DurationParsed): number {
	return duration.d * 86_400_000 + duration.h * 3_600_000 + duration.m * 60_000 + duration.s * 1_000 + duration.ms;
}

export function normalizeDuration(duration: DurationParsed): DurationParsed {
	// Convert everything to total milliseconds, then redistribute.
	// This handles overflow (90s -> 1m 30s), underflow (1500ms -> 1s 500ms),
	// and mixed signs ({h:1, m:-30} -> {m:30}).
	const totalMs= toMillis(duration);

	if (totalMs === 0) {
		return { d: 0, h: 0, m: 0, s: 0, ms: 0 };
	}

	const sign = totalMs < 0 ? -1 : 1;
	let remaining = Math.abs(totalMs);

	const d = Math.floor(remaining / 86_400_000);
	remaining %= 86_400_000;

	const h = Math.floor(remaining / 3_600_000);
	remaining %= 3_600_000;

	const m = Math.floor(remaining / 60_000);
	remaining %= 60_000;

	const s = Math.floor(remaining / 1_000);
	const ms = remaining % 1_000;

	return {
		d: d * sign,
		h: h * sign,
		m: m * sign,
		s: s * sign,
		ms: ms * sign,
	};
}